import type { CSSProperties } from 'react';
import { FALLBACK_ICON, ICONS, isIconName } from './icons';
import type { IconName } from './icons';

/**
 * Icon — renders a Lucide icon by PascalCase name.
 *
 * The prototype resolved icons off the Lucide UMD global loaded from a CDN.
 * Here they come from `lucide-react` through an explicit registry (see
 * ./icons.ts), so only the glyphs this app actually uses ship — but the
 * name-based API is unchanged, which matters because icon names travel with the
 * data (log entry types, prep items, activity rows).
 */

export type { IconName };
export { isIconName };

export type IconProps = {
  name: IconName | string;
  size?: number;
  strokeWidth?: number;
  color?: string;
  className?: string;
  style?: CSSProperties;
};

export function Icon({
  name,
  size = 24,
  strokeWidth = 2,
  color = 'currentColor',
  className = '',
  style,
}: IconProps) {
  const Glyph = isIconName(name) ? ICONS[name] : FALLBACK_ICON;

  return (
    <Glyph
      width={size}
      height={size}
      strokeWidth={strokeWidth}
      color={color}
      className={`ha-icon ${className}`.trim()}
      aria-hidden
      style={{ display: 'inline-block', flexShrink: 0, ...style }}
    />
  );
}
